import { useCallback, useEffect, useState } from "react";
import type React from "react";
import type { FeedbackCategory, FeedbackResponse } from "@excalidraw-ms/shared";
import { Button } from "@excalidraw-ms/shared-ui";
import { FEEDBACK_SCREENSHOT_EXCLUDE_ATTR } from "../utils/captureAppScreenshot";
import type { BackendCapabilityState } from "../auth/msalClient";
import { isBackendAccessDenied } from "../auth/msalClient";
import { BackendError } from "../api/backendClient";

interface FeedbackDialogProps {
  onClose: () => void;
  onSubmit: (payload: {
    category: FeedbackCategory;
    description: string;
    screenshot?: string;
  }) => Promise<FeedbackResponse>;
  teamsChatUrl: string;
  /**
   * Optional async callback that captures a screenshot (e.g. of the current app
   * view) and returns it as a data URL. When omitted, the screenshot UI is
   * hidden. The user must explicitly opt in to attaching a screenshot — it is
   * never collected automatically because it may contain private information.
   */
  onCaptureScreenshot?: () => Promise<string | null>;
  backendCapabilityState?: BackendCapabilityState;
}

const CATEGORIES: FeedbackCategory[] = ["Bug", "Feature Request", "General"];
const FEEDBACK_DIALOG_TITLE_ID = "feedback-dialog-title";

function feedbackFailure(error: unknown, hasTeams: boolean): string {
  const backend = error instanceof BackendError ? error : undefined;
  const contact = hasTeams ? "Contact us on Teams" : "Contact Excalidraw-MS support";
  let message: string;
  if (backend?.status === 400) {
    message =
      "Feedback could not be submitted. Check the description and screenshot, then try again.";
  } else if (backend?.status === 413) {
    message =
      "Feedback could not be submitted. Reduce the description or screenshot size, then try again.";
  } else if (backend?.status === 429) {
    message = "Feedback could not be submitted. Wait before trying again.";
  } else if (backend?.status === 401) {
    message = "Sign in again to submit feedback.";
  } else if (backend?.status === 403 || isBackendAccessDenied(error)) {
    message = "Submitting feedback requires Excalidraw-MS access.";
  } else if (
    backend?.code === "FEEDBACK_UNAVAILABLE" ||
    backend?.code === "FEEDBACK_NOT_CONFIGURED"
  ) {
    message = `Feedback could not be submitted. ${contact}.`;
  } else {
    message = `We couldn't confirm whether your feedback was received. ${contact} before submitting again.`;
  }
  return backend?.requestId ? `${message} Support request: ${backend.requestId}.` : message;
}

export default function FeedbackDialog({
  onClose,
  onSubmit,
  teamsChatUrl,
  onCaptureScreenshot,
  backendCapabilityState = "full",
}: FeedbackDialogProps) {
  const [category, setCategory] = useState<FeedbackCategory>("General");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<FeedbackResponse | null>(null);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");
  const [includeScreenshot, setIncludeScreenshot] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const issueNumber = submitted?.issueNumber;
  const attachmentMessage =
    submitted?.screenshotStatus === "unavailable"
      ? "Your feedback has been received, but the screenshot could not be attached."
      : submitted?.screenshotStatus === "unconfirmed"
        ? "Your feedback has been received. We couldn't confirm whether the screenshot is attached."
        : null;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [handleKeyDown]);

  const handleToggleScreenshot = useCallback(
    async (checked: boolean) => {
      setIncludeScreenshot(checked);
      setScreenshotError(null);
      if (!checked) {
        setScreenshot(null);
        return;
      }
      if (!onCaptureScreenshot) return;
      setCapturingScreenshot(true);
      try {
        const dataUrl = await onCaptureScreenshot();
        if (!dataUrl) {
          setScreenshotError("Could not capture a screenshot of the current screen.");
          setIncludeScreenshot(false);
          return;
        }
        setScreenshot(dataUrl);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Could not capture a screenshot.";
        setScreenshotError(message);
        setIncludeScreenshot(false);
      } finally {
        setCapturingScreenshot(false);
      }
    },
    [onCaptureScreenshot],
  );

  const submit = async () => {
    if (capturingScreenshot || (includeScreenshot && !screenshot)) {
      return;
    }
    const trimmedDescription = description.trim();
    if (!trimmedDescription) {
      setError("Please describe your feedback");
      return;
    }
    if (backendCapabilityState === "local-only") {
      setError(
        "Submitting feedback requires Excalidraw-MS access. You can still contact us on Teams.",
      );
      return;
    }
    if (backendCapabilityState !== "full") {
      setError("Feedback submission is temporarily unavailable. Please try again later.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await onSubmit({
        category,
        description: trimmedDescription,
        ...(includeScreenshot && screenshot ? { screenshot } : {}),
      });
      setSubmitted(response);
    } catch (error) {
      setError(feedbackFailure(error, Boolean(teamsChatUrl)));
    } finally {
      setSubmitting(false);
    }
  };

  const copyReference = async (issueNumber: number) => {
    try {
      await navigator.clipboard.writeText(`#${issueNumber}`);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("failed");
    }
  };

  return (
    <div
      style={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-app-usage-surface="feedback"
      aria-labelledby={FEEDBACK_DIALOG_TITLE_ID}
      {...{ [FEEDBACK_SCREENSHOT_EXCLUDE_ATTR]: "true" }}
    >
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()} data-testid="feedback-dialog">
        <h2 id={FEEDBACK_DIALOG_TITLE_ID} style={titleStyle}>
          Feedback to Excalidraw-MS
        </h2>
        <div style={bodyStyle}>
          {submitted ? (
            <div style={receiptStyle}>
              <div>
                <h3 style={receiptTitleStyle}>Thanks for your feedback!</h3>
                <p
                  role={attachmentMessage ? "alert" : undefined}
                  style={
                    attachmentMessage
                      ? { ...receiptTextStyle, color: "var(--exms-danger-text)" }
                      : receiptTextStyle
                  }
                >
                  {attachmentMessage ??
                    "Your feedback has been received. No further action is needed."}
                </p>
              </div>
              {issueNumber && (
                <div style={referenceRowStyle}>
                  <span style={referenceStyle}>
                    Feedback reference: <strong>#{issueNumber}</strong>
                  </span>
                  <Button onClick={() => void copyReference(issueNumber)} variant="primary">
                    {copyStatus === "copied" ? "Copied!" : "Copy reference"}
                  </Button>
                </div>
              )}
              <p
                role="status"
                style={{
                  ...(copyStatus === "failed" ? errorStyle : copyStatusStyle),
                  ...(copyStatus === "idle" ? { margin: -8 } : {}),
                }}
              >
                {copyStatus === "failed"
                  ? "Could not copy. Please select and copy the reference above."
                  : copyStatus === "copied"
                    ? "Reference copied."
                    : ""}
              </p>
              {teamsChatUrl && (
                <p style={receiptTextStyle}>
                  {submitted.issueNumber
                    ? "If you'd like to follow up, share this reference in our Teams channel."
                    : "If you'd like to follow up, contact us in our Teams channel."}
                </p>
              )}
              {teamsChatUrl && (
                <a
                  href={teamsChatUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...teamsLinkStyle, color: "var(--exms-link-color)" }}
                >
                  Open Teams channel
                </a>
              )}
            </div>
          ) : (
            <>
              <label style={labelStyle}>
                Category
                <select
                  aria-label="Feedback category"
                  style={inputStyle}
                  value={category}
                  onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
                >
                  {CATEGORIES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label style={labelStyle}>
                Description
                <textarea
                  aria-label="Feedback description"
                  style={textAreaStyle}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell us what happened or what you'd like to see."
                />
              </label>

              {onCaptureScreenshot && (
                <div style={screenshotSectionStyle}>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      aria-label="Attach a screenshot of the current screen"
                      checked={includeScreenshot}
                      disabled={capturingScreenshot}
                      onChange={(e) => void handleToggleScreenshot(e.target.checked)}
                    />
                    <span>Attach a screenshot of the current screen (optional)</span>
                  </label>
                  <p style={screenshotHintStyle}>
                    Screenshots may include private information. Only attach one if you're
                    comfortable sharing what's currently visible in the app.
                  </p>
                  {capturingScreenshot && <p style={screenshotHintStyle}>Capturing screenshot…</p>}
                  {screenshotError && <p style={errorStyle}>{screenshotError}</p>}
                  {includeScreenshot && screenshot && (
                    <div>
                      <img
                        src={screenshot}
                        alt="Screenshot preview"
                        style={screenshotPreviewStyle}
                      />
                    </div>
                  )}
                </div>
              )}

              {teamsChatUrl && (
                <a
                  href={teamsChatUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={teamsLinkStyle}
                >
                  Chat with us on Teams
                </a>
              )}

              {error && (
                <p role="alert" style={errorStyle}>
                  {error}
                </p>
              )}

              <div style={buttonRowStyle}>
                <button
                  onClick={() => void submit()}
                  disabled={submitting || capturingScreenshot || (includeScreenshot && !screenshot)}
                  style={primaryButtonStyle}
                >
                  {submitting ? "Submitting…" : "Submit"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const EXCALIDRAW_FONT =
  "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";

const MODAL_SHADOW = "var(--exms-shadow-modal)";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10001,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 520,
  background: "var(--island-bg-color)",
  border: "1px solid var(--dialog-border-color)",
  borderRadius: 12,
  boxShadow: MODAL_SHADOW,
  fontFamily: EXCALIDRAW_FONT,
  padding: 24,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleStyle: React.CSSProperties = { margin: 0, fontSize: 20, fontWeight: 700 };

const bodyStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 0,
};

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  fontSize: 13,
};
const inputStyle: React.CSSProperties = {
  padding: 8,
  borderRadius: 8,
  border: "1px solid var(--dialog-border-color)",
  fontSize: 14,
  fontFamily: "inherit",
};
const textAreaStyle: React.CSSProperties = {
  minHeight: 120,
  resize: "vertical",
  padding: 8,
  borderRadius: 8,
  border: "1px solid var(--dialog-border-color)",
  fontSize: 14,
  fontFamily: "inherit",
};
const teamsLinkStyle: React.CSSProperties = { fontSize: 13 };
const errorStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: "var(--exms-danger-text)",
};
const buttonRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 8,
};
const primaryButtonStyle: React.CSSProperties = {
  borderRadius: 8,
  border: "none",
  padding: "8px 12px",
  cursor: "pointer",
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
  fontSize: "0.875rem",
  fontFamily: "inherit",
  fontWeight: 600,
  letterSpacing: "0.4px",
};
const receiptStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: "8px 0 0",
};
const receiptTitleStyle: React.CSSProperties = {
  margin: "0 0 8px",
  fontSize: 18,
  fontWeight: 600,
};
const receiptTextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.5,
  color: "var(--color-gray-60)",
};
const referenceRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 12,
  padding: 12,
  border: "1px solid var(--dialog-border-color)",
  borderRadius: 8,
};
const referenceStyle: React.CSSProperties = { fontSize: 14 };
const copyStatusStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 12,
  color: "var(--color-gray-60)",
};

const screenshotSectionStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  padding: 8,
  borderRadius: 8,
  border: "1px solid var(--dialog-border-color)",
  background: "var(--island-bg-color)",
};

const checkboxLabelStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  cursor: "pointer",
};

const screenshotHintStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 12,
  color: "var(--color-gray-60)",
};

const screenshotPreviewStyle: React.CSSProperties = {
  marginTop: 6,
  maxWidth: "100%",
  maxHeight: 160,
  borderRadius: 6,
  border: "1px solid var(--dialog-border-color)",
  display: "block",
};
